import { z } from "zod";

import {
  EQUITY_TIME_IN_FORCE_OPTIONS,
  ORDER_TYPES,
  type OrderType,
  QUANTITY_TYPES,
  type QuantityType,
  TIME_IN_FORCE,
  type TimeInForce,
  TOKENIZED_EQUITY_TIME_IN_FORCE_OPTIONS,
  TRAIL_TYPES,
} from "~/v2/constants/equityOrder";
import { isGTZero } from "../shared/utils";

export const zEquityOrderTicketSchema = z.object({
  orderType: z.enum(ORDER_TYPES),
  timeInForce: z.enum(TIME_IN_FORCE),
  trailType: z.enum(TRAIL_TYPES),
  trailValue: z.coerce.number().optional(),
  quantityType: z.enum(QUANTITY_TYPES),
  quantity: z.coerce.number(),
  limitPrice: z.coerce.number().optional(),
  stopPrice: z.coerce.number().optional(),
  isExtendedHours: z.boolean(),
});

export type EquityOrderTicketSchema = z.infer<typeof zEquityOrderTicketSchema>;

export const isTimeInForceValid = ({
  timeInForce,
  orderType,
  quantityType,
  quantity,
  isTokenizedEquity = false,
}: {
  timeInForce: TimeInForce;
  orderType: OrderType;
  quantityType: QuantityType;
  quantity: number | string;
  isTokenizedEquity?: boolean;
}) => {
  if (isTokenizedEquity) {
    return (
      TOKENIZED_EQUITY_TIME_IN_FORCE_OPTIONS.find(
        (option) => option.value === timeInForce,
      )?.orderTypes.includes(orderType) ?? false
    );
  }

  return (
    (EQUITY_TIME_IN_FORCE_OPTIONS.find(
      (option) => option.value === timeInForce,
    )?.orderTypes.includes(orderType) ??
      false) &&
    // Notional and fractional orders must be timeInForce "day"
    (quantityType === "dollars" || !Number.isInteger(Number(quantity))
      ? timeInForce === "day"
      : true)
  );
};

export const equityOrderTicketSchemaSuperRefinement = (
  {
    orderType,
    timeInForce,
    trailType,
    trailValue,
    quantity,
    quantityType,
    limitPrice,
    stopPrice,
  }: EquityOrderTicketSchema,
  refinementContext: z.RefinementCtx,
  options?: { isTokenizedEquity?: boolean },
) => {
  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    !isGTZero(limitPrice)
  ) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Limit Price must be greater than 0",
      path: ["limitPrice"],
    });
  }

  if (
    (orderType === "stop" || orderType === "stop_limit") &&
    !isGTZero(stopPrice)
  ) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Stop Price must be greater than 0",
      path: ["stopPrice"],
    });
  }

  if (orderType === "trailing_stop" && !isGTZero(trailValue)) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Trailing value must be greater than 0",
      path: ["trailValue"],
    });
  }

  if (trailType === "trail_percent" && (trailValue ?? 0) < 0.1) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Trail Percent must be greater than or equal to 0.1",
      path: ["trailValue"],
    });
  }

  if (!isGTZero(quantity)) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Quantity must be greater than 0",
      path: ["quantity"],
    });
  }

  if (
    !isTimeInForceValid({
      timeInForce,
      orderType,
      quantityType,
      quantity,
      isTokenizedEquity: options?.isTokenizedEquity,
    })
  ) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Time in force is not valid",
      path: ["timeInForce"],
    });
  }
};
